import { FC, useMemo, useState } from 'react';
import { ElicitationAnswer, ElicitationQuestion } from '@/types/assistant';

interface Props {
  questions: ElicitationQuestion[];
  onSubmit: (answers: ElicitationAnswer[]) => void;
  /** Enquanto retoma o stream (resposta enviada) — desabilita o card. */
  submitting?: boolean;
}

interface QuestionState {
  selected: string[];
  other: string;
}

function hasOptions(q: ElicitationQuestion): boolean {
  return (q.options?.length ?? 0) > 0;
}

/** Sem opções → texto livre obrigatório; com opções → "outro" só se allow_other. */
function showOther(q: ElicitationQuestion): boolean {
  return !!q.allow_other || !hasOptions(q);
}

function isAnswered(state: QuestionState): boolean {
  return state.selected.length > 0 || state.other.trim() !== '';
}

/**
 * Card de elicitação (E02): renderiza as perguntas do bot (ask_user) — chips de
 * múltipla escolha (single/multi conforme `multi_select`) + campo "Outro" (texto
 * livre) quando `allow_other`. "Responder" só habilita com TODAS respondidas.
 * Genérico — vale pra qualquer assistente (H.Bot, H.Lex, H.Find, H.Deck).
 */
const ElicitationCard: FC<Props> = ({ questions, onSubmit, submitting = false }) => {
  const [answers, setAnswers] = useState<QuestionState[]>(() =>
    questions.map(() => ({ selected: [], other: '' })),
  );

  const allAnswered = useMemo(() => answers.every(isAnswered), [answers]);

  const toggleOption = (qi: number, label: string) => {
    setAnswers((prev) =>
      prev.map((a, i) => {
        if (i !== qi) return a;
        const multi = questions[qi].multi_select;
        if (multi) {
          const selected = a.selected.includes(label)
            ? a.selected.filter((l) => l !== label)
            : [...a.selected, label];
          return { ...a, selected };
        }
        // Single-select: troca a seleção e limpa o "outro" (escolha única).
        return { selected: [label], other: '' };
      }),
    );
  };

  const setOther = (qi: number, text: string) => {
    setAnswers((prev) =>
      prev.map((a, i) => {
        if (i !== qi) return a;
        // Single-select: digitar no "outro" limpa as opções (uma resposta só).
        if (text.trim() && !questions[qi].multi_select) {
          return { selected: [], other: text };
        }
        return { ...a, other: text };
      }),
    );
  };

  const handleSubmit = () => {
    if (!allAnswered || submitting) return;
    onSubmit(
      questions.map((q, i) => ({
        question: q.question,
        selected: answers[i].selected,
        ...(answers[i].other.trim() ? { other: answers[i].other.trim() } : {}),
      })),
    );
  };

  return (
    <div
      data-testid="elicitation-card"
      className="mx-auto max-w-2xl rounded-2xl border border-accent/30 bg-surface-secondary p-4 md:p-5 space-y-5"
    >
      <div className="flex items-center gap-2 text-content-primary">
        <span className="material-symbols-outlined text-accent text-[20px]">help</span>
        <span className="text-sm font-semibold">Preciso de algumas informações</span>
      </div>

      {questions.map((q, qi) => (
        <div key={qi} className="space-y-2" data-testid="elicitation-question">
          <p className="text-sm font-medium text-content-primary">{q.question}</p>

          {hasOptions(q) && (
            <div className="flex flex-wrap gap-2">
              {q.options!.map((opt) => {
                const active = answers[qi].selected.includes(opt.label);
                return (
                  <button
                    key={opt.label}
                    type="button"
                    data-testid="elicitation-option"
                    aria-pressed={active}
                    disabled={submitting}
                    title={opt.description}
                    onClick={() => toggleOption(qi, opt.label)}
                    className={`px-3 py-1.5 rounded-full border text-sm transition-colors disabled:opacity-50 ${
                      active
                        ? 'bg-accent text-accent-contrast border-accent'
                        : 'bg-surface-primary text-content-secondary border-surface-border hover:border-accent'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          )}

          {showOther(q) && (
            <input
              type="text"
              data-testid="elicitation-other"
              disabled={submitting}
              value={answers[qi].other}
              onChange={(e) => setOther(qi, e.target.value)}
              placeholder={hasOptions(q) ? 'Outro (especifique)…' : 'Sua resposta…'}
              className="w-full rounded-lg border border-surface-border bg-surface-primary px-3 py-2 text-sm text-content-primary placeholder:text-content-muted focus:border-accent focus:outline-none disabled:opacity-50"
            />
          )}
        </div>
      ))}

      <div className="flex justify-end">
        <button
          type="button"
          data-testid="elicitation-submit"
          disabled={!allAnswered || submitting}
          onClick={handleSubmit}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-accent text-accent-contrast text-sm font-medium transition-opacity disabled:opacity-40"
        >
          {submitting ? (
            <span className="material-symbols-outlined text-[18px] animate-spin">progress_activity</span>
          ) : (
            <span className="material-symbols-outlined text-[18px]">send</span>
          )}
          Responder
        </button>
      </div>
    </div>
  );
};

export default ElicitationCard;
